/**
 * Core Values Section
 * Fetched from CMS API
 */
import React, { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import axios from 'axios';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const CORE_VALUES_CATEGORY_ID = 39;

const CoreValues = () => {
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch core values from CMS API
  useEffect(() => {
    const fetchCoreValues = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}info/by-category`,
          { CategoryId: CORE_VALUES_CATEGORY_ID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data)) {
          // Filter: only show items that are NOT deleted AND are published
          const filteredItems = response.data.data.filter(
            (item) => item.IsDeleted === false && item.IsPublished === true
          );
          // Sort by SortOrder
          filteredItems.sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0));

          const apiValues = filteredItems.map((item, index) => ({
            icon: LucideIcons[item.Image],
            title: item.Title || '',
            description: (item.Description || '')
              .replace(/<[^>]*>/g, '')
              .replace(/&nbsp;/g, ' '),
          }));

          setValues(apiValues);
        }
      } catch (error) {
        console.error('Failed to fetch core values:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCoreValues();
  }, []);

  if (loading) {
    return (
      <section className="py-12 md:py-5 bg-[#f4f4f4]">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <div className="h-8 w-48 bg-gray-200 rounded mx-auto animate-pulse" />
            <div className="w-16 h-0.5 bg-gray-200 mx-auto mt-3 animate-pulse" />
          </div>
          <div className="flex flex-col md:flex-row">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="flex-1 flex flex-col items-center text-center px-4 py-6">
                <div className="w-10 h-10 bg-gray-200 rounded mb-3 animate-pulse" />
                <div className="h-4 w-20 bg-gray-200 rounded mb-2 animate-pulse" />
                <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (values.length === 0) {
    return null;
  }
  return (
    <section className="py-12 md:py-5 bg-[#f4f4f4]">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-[#2f3951]">
            Our Core Values
          </h2>
          <div className="w-16 h-0.5 bg-[#ca9a37] mx-auto mt-3"></div>
        </div>

        {/* Values Row with vertical dividers */}
        <div className="flex flex-col md:flex-row">
          {values.map((value, index) => {
            const Icon = value.icon;
            const isLast = index === values.length - 1;
            return (
              <div
                key={index}
                className={`flex-1 flex flex-col items-center text-center px-4 py-6 md:px-6 md:py-0 ${
                  !isLast ? 'border-b md:border-b-0 md:border-r border-gray-300' : ''
                }`}
              >
                <div className="w-10 h-10 text-[#ca9a37] flex items-center justify-center mb-3">
                  {Icon && <Icon className="w-8 h-8" strokeWidth={1.5} />}
                </div>
                <h3 className="text-sm md:text-base font-semibold text-[#2f3951] mb-2">
                  {value.title}
                </h3>
                <p className="text-xs md:text-sm text-gray-500 leading-relaxed max-w-[200px]">
                  {value.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CoreValues;
